import { getHeaderActiveColumns, TD } from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ActiveIcon from "../../../../icons/ActiveIcon";
import DocumentIcon from "../../../../icons/DocumentIcon";
import TableBasic, { TableRow } from "../../../../Table/TableBasic";
import DocumentModal from "../../../../Modal/modals/DocumentModal";
import IngenRegistreringer from "../IngenRegistreringer";
import { PersonDto } from "../../../../../interfaces/schema";
import { PersonEfteruddannelseField } from "../../../../../interfaces/personer";
import { IColumn, SortBy } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { periodeIsActive, stringDateToPP } from "../../../../../util/date";
import EfteruddannelseEditFormModal from "./EfteruddannelseEditFormModal";
import VisPeriodeModal from "./VisPeriodeModal";
import { useMemo } from "react";
import Spinner from "../../../../spinner/Spinner";
import { useGenberegnEfteruddannelsePeriodeMutation } from "../../../../../hooks/mutations/useGenberegnEfteruddannelsePeriodeMutation";
import DownloadListIcon from "../../../../icons/DownloadListIcon";
import SyncIcon from "../../../../icons/SyncIcon";
import { decoratePersonEfteruddannelse } from "../Efteruddannelse/decoratePeriod";
import { orderBy } from "../../../../../util/orderBy";

export default function Efteruddannelse({
  columns,
  defaultColumns,
  person,
  personId,
  setActiveColumns,
  invalidateData,
}: {
  personId: string;
  person: PersonDto;
  defaultColumns: IColumn<PersonEfteruddannelseField>[];
  columns: IColumn<PersonEfteruddannelseField>[];
  setActiveColumns: React.Dispatch<
    React.SetStateAction<PersonEfteruddannelseField[]>
  >;
  invalidateData: InvalidateDataFunction<any>;
}) {
  const {
    mutate: opretIndberetninger,
    isPending: isOpretIndberetningerLoading,
  } = useGenberegnEfteruddannelsePeriodeMutation(person.id, invalidateData);

  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personEfteruddanelseSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  const personEfteruddannelse = useMemo(() => {
    const temp = person.personEfteruddannelse?.map((pe) => ({
      ...decoratePersonEfteruddannelse(pe, person.personEfteruddannelse ?? []),
    }));
    const periodeUdloebsIndberetninger = temp?.filter((pe) =>
      pe.personEfteruddannelseIndberetning.find(
        (i) => i.efteruddannelseIndberetningstype === "PERIODEUDLOEB",
      ),
    );
    // Sorter efter indberetningsdato
    const sorted = orderBy(
      periodeUdloebsIndberetninger,
      "periode.tilDatoInklusiv",
      "desc",
    );
    if (sorted.length > 0) {
      sorted[0].kanSletteIndberetninger = true;
    }
    return temp;
  }, [person.personEfteruddannelse]);

  if (!personEfteruddannelse || !personEfteruddannelse.length) {
    return <IngenRegistreringer />;
  }

  // event handlers
  function downloadHandler() {
    if (personEfteruddannelse) {
      tableToXLSX(columns, personEfteruddannelse, {
        fileName: `efteruddannelse-for-${person.kundeNr}`,
        additionalData: {
          kundenr: person.kundeNr,
        },
      });
    }
  }

  return (
    <>
      <div className="c-download-button d-flex gap-3">
        <button
          title="Genberegn perioder og indberetninger"
          className="btn btn--action"
          onClick={() => {
            opretIndberetninger();
          }}
          disabled={isOpretIndberetningerLoading}
        >
          {isOpretIndberetningerLoading && <Spinner size={14} />}
          Genberegn <SyncIcon />
        </button>
        <button className="btn btn--action" onClick={downloadHandler}>
          Hent <DownloadListIcon width={19} />
        </button>
      </div>
      <TableBasic
        columns={columns}
        setActiveColumns={setActiveColumns}
        defaultColumns={defaultColumns}
        className="table--clear-empty-columns"
        headerRow={
          <>
            <th className="indicator"></th>
            {getHeaderActiveColumns(columns, {
              defaultSortBy,
              setSortBy,
              sortBy,
            })}
            <th>Erklæring</th>
            <th>Periode</th>
          </>
        }
      >
        {orderBy(
          personEfteruddannelse,
          sortBy.map((ds) => ds.field),
          sortBy.map((ds) => ds.direction),
        ).map((uddannelse) => {
          return (
            <TableRow key={uddannelse.id}>
              <td
                className={`indicator${
                  periodeIsActive(
                    new Date(uddannelse.periode.fraDato),
                    new Date(uddannelse.periode.tilDatoInklusiv),
                  )
                    ? " has-effect"
                    : ""
                }`}
              >
                <ActiveIcon />
              </td>
              {columns
                .filter((col) => col.active)
                .map((col) => {
                  return <TD key={col.field} col={col} rowData={uddannelse} />;
                })}
              <td>
                {!!uddannelse.personEfteruddannelseIndberetning.find(
                  (indberetning) =>
                    indberetning.efteruddannelseIndberetningstype ===
                    "PERIODEUDLOEB",
                ) && (
                  <DocumentModal
                    getDocument={`person/${personId}/efteruddannelser/${uddannelse.id}/dokument`}
                    downloadLink={`person/${personId}/efteruddannelser/${uddannelse.id}/download`}
                    title={
                      <>
                        <DocumentIcon height={20} />
                        Efteruddannelse
                      </>
                    }
                    buttonContents={[
                      <DocumentIcon key={1} />,
                      <span key={2}>Vis erklæring</span>,
                    ]}
                    contentProps={{
                      className: "c-document c-document--efteruddannelse",
                      style: { whiteSpace: "pre-wrap" },
                    }}
                  />
                )}
              </td>
              <td>
                <VisPeriodeModal
                  title="Periode"
                  personId={personId}
                  efteruddannelsesPeriodeId={uddannelse.id}
                  description={`Måneder der danner grundlag for perioden ${stringDateToPP(
                    uddannelse.periode.fraDato,
                  )} - ${stringDateToPP(uddannelse.periode.tilDatoInklusiv)}`}
                />
              </td>
              <td className="edit-column">
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <EfteruddannelseEditFormModal
                    personId={person.id}
                    efteruddannelseId={uddannelse.id}
                    efteruddannelseIndberetningId={uddannelse.indberetningId}
                    invalidateData={invalidateData}
                    kanSletteIndberetning={Boolean(
                      uddannelse.kanSletteIndberetninger,
                    )}
                  />
                </div>
              </td>
            </TableRow>
          );
        })}
      </TableBasic>
    </>
  );
}
const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "periode.tilDatoInklusiv",
  },
  {
    direction: "desc",
    field: "periode.fraDato",
  },
];
